import { Address } from 'viem'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type FailedSubscription = {
    spaceAddress: Address
    tokenId: string
    attemptedAt: number
    error?: string
}

export type MembershipSubscriptionsStore = {
    failedSubscriptions: FailedSubscription[]
    addFailedSubscription: (spaceAddress: Address, tokenId: string, error?: string) => void
    removeFailedSubscription: (spaceAddress: Address, tokenId: string) => void
    getFailedSubscriptionsBySpace: (spaceAddress: Address) => FailedSubscription[]
    clearFailedSubscriptions: () => void
    // Track spaces we've already checked for currency migration (no need to check again)
    checkedMigrationSpaces: Address[]
    addCheckedMigrationSpace: (spaceAddress: Address) => void
}

export const EMPTY_TOKEN_ID = 'EMPTY_TOKEN_ID'

export const useMembershipSubscriptionsStore = create<MembershipSubscriptionsStore>()(
    persist(
        (set, get) => ({
            failedSubscriptions: [],
            addFailedSubscription: (spaceAddress, tokenId, error) => {
                set((state) => ({
                    failedSubscriptions: [
                        ...state.failedSubscriptions.filter(
                            (sub) =>
                                !(sub.spaceAddress === spaceAddress && sub.tokenId === tokenId),
                        ),
                        {
                            spaceAddress,
                            tokenId,
                            attemptedAt: Date.now(),
                            error,
                        },
                    ],
                }))
            },
            removeFailedSubscription: (spaceAddress, tokenId) => {
                set((state) => ({
                    failedSubscriptions: state.failedSubscriptions.filter(
                        (sub) => !(sub.spaceAddress === spaceAddress && sub.tokenId === tokenId),
                    ),
                }))
            },
            getFailedSubscriptionsBySpace: (spaceAddress) => {
                return get().failedSubscriptions.filter((sub) => sub.spaceAddress === spaceAddress)
            },
            clearFailedSubscriptions: () => set({ failedSubscriptions: [] }),
            checkedMigrationSpaces: [],
            addCheckedMigrationSpace: (spaceAddress) => {
                const normalized = spaceAddress.toLowerCase() as Address
                set((state) => ({
                    checkedMigrationSpaces: state.checkedMigrationSpaces.includes(normalized)
                        ? state.checkedMigrationSpaces
                        : [...state.checkedMigrationSpaces, normalized],
                }))
            },
        }),
        {
            name: 'town/membership-subscriptions',
            version: 1,
            partialize: (state) => ({
                failedSubscriptions: state.failedSubscriptions,
                checkedMigrationSpaces: state.checkedMigrationSpaces,
            }),
        },
    ),
)
